/**
 * Registers, places the one test call, and measures it.
 *
 * Uses our JsSIP fork, not upstream: the fork carries the STUN/SDP handling and the ICE gathering
 * watchdog this client depends on. Do not write another watchdog here — with
 * `forceUseStunResponse` the fork already proceeds with whatever candidates it has after 2 s on
 * the offer path, because on some networks Chrome never delivers a srflx candidate and gathering
 * never completes on its own.
 *
 * JsSIP debug logging stays off in production: its output contains full SIP messages, and those
 * carry the session's credentials.
 */

import JsSIP from '@per_moeller/jssip-telecomx'
import { STATS_INTERVAL_MS, TIMEOUTS } from '@/config/targets'
import { extractSample, type StatsSample } from './stats'
import type { CallTestSession } from './types'

export type CallPhase = 'registering' | 'dialling' | 'ringing' | 'in-call' | 'ended'

export type CallFailure =
	/** `disconnected` before ever reaching `registered` — the network blocks wss. */
	| 'wss-blocked'
	| 'registration-failed'
	| 'call-rejected'
	| 'no-answer'
	| 'media-failed'

export interface CallHooks {
	onPhase(phase: CallPhase): void
	onSample(sample: StatsSample): void
	/** Fired once the remote audio is attached, so a level meter can be started on it. */
	onRemoteStream(stream: MediaStream): void
	/** The generated signal being sent, so the page can show it really is producing audio. */
	onLocalStream(stream: MediaStream): void
}

export interface CallRunOutcome {
	connected: boolean
	failure: CallFailure | null
	samples: StatsSample[]
	/** Answer to hangup, as the browser saw it. */
	mediaMs: number
}

export interface RunCallOptions {
	session: CallTestSession
	/** The element the remote audio is attached to; required for both hearing and analysing it. */
	audioEl: HTMLAudioElement
	hooks: CallHooks
	/** Aborts the run — the visitor pressing "stop test". */
	signal: AbortSignal
}

export async function runCall(options: RunCallOptions): Promise<CallRunOutcome> {
	const { session, audioEl, hooks, signal } = options

	JsSIP.debug.disable()

	// No getUserMedia: the microphone is not what this page measures. A generated track runs
	// through the same encoder, the same RTP flow and the same NAT traversal, so it proves the
	// network path just as well — while sparing a non-technical visitor a permission prompt and
	// removing a variable (a muted or broken microphone) that has nothing to do with the firewall.
	const signalSource = createTestSignal()
	hooks.onLocalStream(signalSource.stream)

	const host = session.server.split('://')[1] ?? session.server
	const ua = new JsSIP.UA({
		sockets: [new JsSIP.WebSocketInterface(session.server)],
		uri: `sip:${session.username}@${host}`,
		password: session.password,
		realm: session.realm,
		display_name: session.displayName,
		user_agent: 'Connection Check',
		session_timers_refresh_method: 'invite'
	})

	const samples: StatsSample[] = []
	let outcome: CallRunOutcome = { connected: false, failure: null, samples, mediaMs: 0 }

	try {
		hooks.onPhase('registering')
		await register(ua, signal)

		hooks.onPhase('dialling')
		outcome = {
			...outcome,
			...await placeCall({
				ua, session, host, localStream: signalSource.stream, audioEl, hooks, samples, signal
			})
		}
	} catch (error) {
		outcome.failure = error instanceof CallError ? error.failure : 'media-failed'
	} finally {
		hooks.onPhase('ended')
		signalSource.stop()
		try { ua.stop() } catch { /* already down */ }
	}

	return outcome
}

/**
 * The audio the browser sends for the platform to measure.
 *
 * A 440 Hz carrier, chosen to sit clear of the frequencies a telephony network reacts to: every
 * DTMF pair, the 1100 Hz fax calling tone and the 2100 Hz echo-canceller disable tone. It is
 * amplitude-modulated at a syllable-like 4 Hz and never reaches zero, because a dead-steady tone
 * is what silence suppression and comfort-noise generators are most likely to mangle.
 */
function createTestSignal(): { stream: MediaStream, stop: () => void } {
	const context = new AudioContext()
	// The run starts from a click, so the context should already be allowed to run; resuming is
	// harmless when it is and necessary when the browser suspended it anyway.
	void context.resume().catch(() => {})

	const destination = context.createMediaStreamDestination()

	const carrier = context.createOscillator()
	carrier.frequency.value = 440

	const modulator = context.createOscillator()
	modulator.frequency.value = 4

	const depth = context.createGain()
	depth.gain.value = 0.13

	const output = context.createGain()
	output.gain.value = 0.18

	// Modulating the gain parameter itself, so amplitude swings between 0.05 and 0.31. That is
	// roughly the level of ordinary speech on a telephone leg: well clear of any silence
	// threshold, never clipping, and never actually reaching zero.
	modulator.connect(depth)
	depth.connect(output.gain)
	carrier.connect(output)
	output.connect(destination)

	carrier.start()
	modulator.start()

	return {
		stream: destination.stream,
		stop() {
			try {
				carrier.stop()
				modulator.stop()
			} catch { /* already stopped */ }
			destination.stream.getTracks().forEach((track) => track.stop())
			void context.close().catch(() => {})
		}
	}
}

class CallError extends Error {
	readonly failure: CallFailure

	constructor(failure: CallFailure) {
		super(failure)
		this.name = 'CallError'
		this.failure = failure
	}
}

function register(ua: JsSIP.UA, signal: AbortSignal): Promise<void> {
	return new Promise((resolve, reject) => {
		let registered = false
		const settle = withTimeout(TIMEOUTS.sipRegister, reject, 'registration-failed')

		ua.on('registered', () => {
			registered = true
			settle(() => resolve())
		})

		ua.on('registrationFailed', () => {
			settle(() => reject(new CallError('registration-failed')))
		})

		// Losing the socket before the first successful registration is the signature of a
		// firewall that blocks wss to unknown hosts, not of a SIP problem.
		ua.on('disconnected', () => {
			if (!registered) settle(() => reject(new CallError('wss-blocked')))
		})

		// Stopping during registration should not leave the visitor waiting out the timeout.
		signal.addEventListener('abort', () => {
			settle(() => reject(new CallError('registration-failed')))
		}, { once: true })

		ua.start()
	})
}

interface PlaceCallArgs {
	ua: JsSIP.UA
	session: CallTestSession
	host: string
	localStream: MediaStream
	audioEl: HTMLAudioElement
	hooks: CallHooks
	samples: StatsSample[]
	signal: AbortSignal
}

function placeCall(args: PlaceCallArgs): Promise<Partial<CallRunOutcome>> {
	const { ua, session, host, localStream, audioEl, hooks, samples, signal } = args

	return new Promise((resolve, reject) => {
		let answeredAt: number | null = null
		let done = false
		let statsTimer: ReturnType<typeof setInterval> | undefined
		let maxTimer: ReturnType<typeof setTimeout> | undefined

		// The answer deadline covers only the wait for an answer. Once the far end picks up, the
		// call legitimately runs for the length of the recorded message, and the backstop below
		// takes over — guarding the whole call with the answer timeout would report every
		// successful call as unanswered.
		const answerTimer = setTimeout(() => {
			if (answeredAt !== null) return
			settle(() => reject(new CallError('no-answer')))
		}, TIMEOUTS.callAnswer)

		const settle = (action: () => void) => {
			if (done) return
			done = true
			clearTimeout(answerTimer)
			clearTimeout(maxTimer)
			clearInterval(statsTimer)
			action()
		}

		const rtcSession = ua.call(`sip:${session.testNumber}@${host}`, {
			mediaConstraints: { audio: true, video: false },
			mediaStream: localStream,
			pcConfig: { iceServers: session.iceServers },
			// Fork-specific, and set on every Communicator call: make the offer advertise the
			// STUN-discovered address, or some networks negotiate a path media never flows over.
			// voiceActivityDetection off matters too — with DTX on, a quiet microphone sends
			// almost nothing and the platform reads that as no audio from the browser.
			rtcOfferConstraints: { forceUseStunResponse: true, voiceActivityDetection: false }
		})

		const finish = (result: Partial<CallRunOutcome>) => settle(() => resolve(result))

		rtcSession.connection.addEventListener('track', (event) => {
			const [stream] = (event as RTCTrackEvent).streams
			if (!stream) return

			audioEl.srcObject = stream
			// The run began with a click, so autoplay is permitted — but a rejection here must not
			// fail the test. The caller shows a "tap for sound" prompt instead.
			void audioEl.play().catch(() => {})
			hooks.onRemoteStream(stream)
		})

		rtcSession.on('progress', () => hooks.onPhase('ringing'))

		rtcSession.on('confirmed', () => {
			answeredAt = Date.now()
			hooks.onPhase('in-call')

			statsTimer = setInterval(() => {
				void rtcSession.connection.getStats().then((report) => {
					const sample = extractSample(report, Date.now())
					samples.push(sample)
					hooks.onSample(sample)
				}).catch(() => {})
			}, STATS_INTERVAL_MS)

			// The platform hangs up when the recorded message finishes; this is only a backstop
			// for a far end that never does. Terminating raises 'ended', which settles normally.
			maxTimer = setTimeout(() => {
				if (!rtcSession.isEnded()) rtcSession.terminate()
			}, TIMEOUTS.callMax)
		})

		rtcSession.on('ended', () => {
			finish({
				connected: answeredAt !== null,
				mediaMs: answeredAt === null ? 0 : Date.now() - answeredAt
			})
		})

		rtcSession.on('failed', () => {
			// Reaching 'failed' after the answer still means media flowed and is worth reporting.
			if (answeredAt !== null) {
				finish({ connected: true, mediaMs: Date.now() - answeredAt })
				return
			}
			finish({ connected: false, failure: 'call-rejected', mediaMs: 0 })
		})

		signal.addEventListener('abort', () => {
			if (!rtcSession.isEnded()) rtcSession.terminate()
		}, { once: true })
	})
}

/**
 * Guards a single-event wait against hanging: whichever of success, failure or timeout arrives
 * first wins, and the rest are ignored. Returns a gate the handlers pass their action through.
 */
function withTimeout(
	ms: number,
	reject: (error: unknown) => void,
	onTimeout: CallFailure
): (action: () => void) => void {
	let done = false

	const timer = setTimeout(() => {
		if (done) return
		done = true
		reject(new CallError(onTimeout))
	}, ms)

	return (action: () => void) => {
		if (done) return
		done = true
		clearTimeout(timer)
		action()
	}
}
